import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import ProfileBar from "../../Organisms/App Utility Bar/profilebar";

import useFidel from "../../CustomHooks/useFidel";
import { useGetFidelCards } from "../../CustomHooks/Queries";

import { getUserId } from "../../Utils/helpers";

import { Card } from "../../Assets/Icons";
import CustomMap from "../../Components/Map";
import { BackIcon, ChevronIcon, ExternalLinkIcon } from "./nn-icons";

import "./neighborhood-network.scss";
import "./neighborhood-network-responsive.scss";

function NeighborhoodNetwork() {
  const navigate = useNavigate();
  const location = useLocation();
  const residentId = getUserId();

  const { isLoadingFidel, linkingCard, LinkCard } = useFidel();
  const { isLoading: isLoadingFidelCards, data: fidelCards } = useGetFidelCards();

  const atleastOneFidelCardLinked = fidelCards?.length > 0;
  const linkedCardCount = fidelCards?.length ?? 0;

  const isLoading = isLoadingFidelCards || isLoadingFidel;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  const handleLinkCard = useCallback(() => {
    LinkCard();
  }, [LinkCard]);

  const hasAutoLinkedCard = useRef(false);

  useEffect(() => {
    if (
      location.state?.startLinkingCardOnMount &&
      !isLoading &&
      !linkingCard &&
      !hasAutoLinkedCard.current
    ) {
      hasAutoLinkedCard.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      handleLinkCard();
    }
  }, [location.state, location.pathname, isLoading, linkingCard, navigate, handleLinkCard]);

  return (
    <div className={`nn-page${animReady ? " anim-ready" : ""}`}>
      <section>
        <div className="max-width-container">
          <ProfileBar />
          {isLoading && (
            <div className="loading-content">
              <CircularProgress />
            </div>
          )}
          {!isLoading && (
            <>
              {!atleastOneFidelCardLinked ? (
                <div className="nn-onboard">
                  <div className="nn-onboard-content">
                    <div className="nn-onboard-icon">
                      <Card />
                    </div>
                    <div className="nn-onboard-text">
                      <span className="nn-onboard-title">Link a card to get started</span>
                      <span className="nn-onboard-sub">
                        Connect a credit or debit card to unlock cash back from local merchants and online brands.
                      </span>
                    </div>
                  </div>
                  <button className="nn-link-btn" onClick={handleLinkCard} disabled={linkingCard}>
                    Link Card{linkingCard && <CircularProgress size={14} />}
                  </button>
                </div>
              ) : (
                <>
                  <div className="nn-content-card">
                    <div className="nn-card-header">
                      <button type="button" className="nn-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
                        <BackIcon />
                      </button>
                      <h1>Neighborhood Cash</h1>
                      <span className="nn-header-spacer" />
                    </div>

                    <div className="nn-map-embed">
                      <button
                        type="button"
                        className="nn-map-embed-header"
                        onClick={() => navigate("/deals/neighborhood-network/offers-near-you")}
                      >
                        <div>
                          <span className="title">Browse merchants near you</span>
                          <span className="sub">Tap to find offers near you</span>
                        </div>
                        <ExternalLinkIcon />
                      </button>
                      <div className="nn-map-embed-body">
                        <CustomMap
                          residentId={residentId}
                          onMapClick={() => navigate("/deals/neighborhood-network/offers-near-you")}
                        />
                      </div>
                    </div>

                    <div className="nn-services">
                      <div
                        className="nn-services-row"
                        onClick={() => navigate("/deals/neighborhood-network/linked-cards")}
                      >
                        <div className="nn-services-icon">
                          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <rect x="2" y="4" width="13" height="9" rx="1.5" stroke="#636879" strokeWidth="1.2" />
                            <path d="M2 7.5h13" stroke="#636879" strokeWidth="1.2" />
                          </svg>
                        </div>
                        <div className="nn-services-info">
                          <span className="title">My Cards</span>
                          <span className="sub">
                            {linkedCardCount} card{linkedCardCount !== 1 ? "s" : ""} linked
                          </span>
                        </div>
                        <ChevronIcon />
                      </div>
                      <div
                        className="nn-services-row"
                        onClick={() => navigate("/deals/neighborhood-network/brands-and-offers")}
                      >
                        <div className="nn-services-icon">
                          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <rect x="2.2" y="3.2" width="12.6" height="10.6" rx="2.2" stroke="#636879" strokeWidth="1.2" />
                            <path d="M5.2 6.4h6.6M5.2 9h3.6" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
                          </svg>
                        </div>
                        <div className="nn-services-info">
                          <span className="title">Brands & Offers</span>
                          <span className="sub">Browse all participating brands</span>
                        </div>
                        <ChevronIcon />
                      </div>
                      <div
                        className="nn-services-row"
                        onClick={() => navigate("/deals/neighborhood-network/rewards")}
                      >
                        <div className="nn-services-icon">
                          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <circle cx="8.5" cy="8.5" r="6.5" stroke="#54FF99" strokeWidth="1.2" />
                            <path d="M6 8.5l2 2 3.5-3.5" stroke="#54FF99" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className="nn-services-info">
                          <span className="title">My Rewards</span>
                          <span className="sub">Track your cash back earnings</span>
                        </div>
                        <ChevronIcon />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default NeighborhoodNetwork;
